/**
 * useExcelDownload Hook
 * 라이더 근무 현황 엑셀 다운로드
 */
import { useState, useCallback } from 'react';
import { riderWorkStatusApi } from '../api/riderWorkStatusApi';
import { RiderWorkStatusFilters } from '../types/riderWorkStatus.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { removeDateHyphens } from '@/shared/utils/dateUtils';

export const useExcelDownload = (isSupplierUser: boolean, userId: string | null) => {
    const { showToast } = useToast();
    const [excelDownloading, setExcelDownloading] = useState(false);

    /**
     * 엑셀 다운로드
     */
    const handleExcelDownload = useCallback(
        async (filters: RiderWorkStatusFilters) => {
            try {
                setExcelDownloading(true);

                // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
                const fromDate = removeDateHyphens(filters.fromDate);
                const toDate = removeDateHyphens(filters.toDate);

                if (!fromDate || !toDate) {
                    showToast('시작일과 종료일을 입력해주세요.', { type: ToastType.WARNING });
                    return;
                }

                // SUPPLIER 사용자는 자동으로 suppId 설정
                let suppId = filters.suppId;
                if (isSupplierUser && userId) {
                    suppId = userId;
                }

                // 최소 근무일수 파싱
                const minWorkDays = filters.minWorkDays ? parseInt(filters.minWorkDays, 10) : undefined;
                const blob = await riderWorkStatusApi.downloadWorkStatusExcel(fromDate, toDate, suppId, minWorkDays);

                // 파일 다운로드
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;

                // 파일명 생성 (YYYYMMDD~YYYYMMDD_라이더근무현황.xlsx)
                link.download = `${fromDate}~${toDate}_라이더근무현황.xlsx`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // 메모리 해제
                window.URL.revokeObjectURL(downloadUrl);

                showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
            } catch {
                showToast('엑셀 파일 다운로드에 실패했습니다.', { type: ToastType.ERROR });
            } finally {
                setExcelDownloading(false);
            }
        },
        [isSupplierUser, userId, showToast],
    );

    return {
        excelDownloading,
        handleExcelDownload,
    };
};
